import React from 'react';
import { Truck } from 'lucide-react';
import { TimeRangeSelector } from '../ui/TimeRangeSelector';
import { MoneyInput } from '../ui/MoneyInput';
import { TextInput } from '../ui/TextInput';
import { Database } from '../../types/database';
import { FORM_BORDER, FORM_BORDER_HOVER, FOCUS_RING, FORM_RADIUS } from '../ui/tokens';

interface Paso2EntregaPagoProps {
  // Forma de entrega - usando tipos de la BD
  formaEntrega: Database['public']['Enums']['tipo_entrega_enum'];
  onFormaEntregaChange: (forma: Database['public']['Enums']['tipo_entrega_enum']) => void;

  // Fecha y hora de entrega (opcionales)
  fechaEntrega: string;
  onFechaEntregaChange: (fecha: string) => void;
  rangoHorario: string;
  onRangoHorarioChange: (rango: string) => void;

  // Dirección de despacho
  direccionDespacho: string;
  onDireccionDespachoChange: (direccion: string) => void;

  /** Bloque "Comprobante" del pago inicial (solo al registrar un pedido nuevo). */
  bloqueComprobante?: React.ReactNode;
  /** Medio de pago y folio, debajo del monto y la fecha del pago. */
  bloqueMedioPago?: React.ReactNode;
  
  // Notas
  notas: string;
  onNotasChange: (notas: string) => void;
  
  // Pago y entrega
  isEditMode: boolean;
  registrarPago: boolean;
  onRegistrarPagoChange: (registrar: boolean) => void;
  montoPagoInicial: number;
  onMontoPagoInicialChange: (monto: number) => void;
  fechaPago: string;
  onFechaPagoChange: (fecha: string) => void;
  registrarEntrega: boolean;
  onRegistrarEntregaChange: (registrar: boolean) => void;
  cantidadEntregaInicial: number;
  onCantidadEntregaInicialChange: (cantidad: number) => void;
  fechaEntregaReal: string;
  onFechaEntregaRealChange: (fecha: string) => void;
  
  // Datos para validación
  montoTotal: number;
  cantidadTotal: number;
  precioUnitario: number;
}

export const Paso2EntregaPago: React.FC<Paso2EntregaPagoProps> = ({
  formaEntrega,
  onFormaEntregaChange,
  fechaEntrega,
  onFechaEntregaChange,
  rangoHorario,
  onRangoHorarioChange,
  direccionDespacho,
  onDireccionDespachoChange,
  bloqueComprobante,
  bloqueMedioPago,
  notas,
  onNotasChange,
  isEditMode,
  registrarPago,
  onRegistrarPagoChange,
  montoPagoInicial,
  onMontoPagoInicialChange,
  fechaPago,
  onFechaPagoChange,
  registrarEntrega,
  onRegistrarEntregaChange,
  cantidadEntregaInicial,
  onCantidadEntregaInicialChange,
  fechaEntregaReal,
  onFechaEntregaRealChange,
  montoTotal,
  cantidadTotal,
  precioUnitario
}) => {
  // Calcular unidades equivalentes al monto actual del slider
  const unidadesSlider = precioUnitario > 0 ? Math.round(montoPagoInicial / precioUnitario) : 0;
  return (
    <div className="space-y-4">
      {/* Forma de Entrega */}
      <div>
        <p className="block text-sm font-semibold text-slate-700 mb-2">
          Forma de Entrega <span className="text-red-500">*</span>
        </p>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="forma_entrega"
              value="retiro"
              checked={formaEntrega === 'retiro'}
              onChange={(e) => onFormaEntregaChange(e.target.value as 'retiro' | 'despacho')}
              className="w-4 h-4 text-emerald-600 border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
            />
            <span className="text-sm font-medium text-slate-700">Retiro</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="forma_entrega"
              value="despacho"
              checked={formaEntrega === 'despacho'}
              onChange={(e) => onFormaEntregaChange(e.target.value as 'retiro' | 'despacho')}
              className="w-4 h-4 text-emerald-600 border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
            />
            <span className="text-sm font-medium text-slate-700">Despacho</span>
          </label>
        </div>
      </div>

      {formaEntrega === 'despacho' && (
        <TextInput
          label="Dirección de Despacho *"
          value={direccionDespacho}
          onChange={onDireccionDespachoChange}
          placeholder="Ingrese dirección completa"
          prefixIcon={<Truck className="w-5 h-5 text-slate-400" />}
          required
        />
      )}

      {/* Fecha de Entrega y Rango Horario en una fila */}
      <div className="flex gap-4">
        <div className="w-48 flex-shrink-0">
          <label htmlFor="paso2-fecha-entrega" className="block text-sm font-medium text-slate-600 mb-1.5">
            Fecha de Entrega
          </label>
          <input
            id="paso2-fecha-entrega"
            type="date"
            value={fechaEntrega}
            onChange={(e) => onFechaEntregaChange(e.target.value)}
            className={`w-full px-4 py-3 ${FORM_BORDER} ${FORM_BORDER_HOVER} ${FOCUS_RING} ${FORM_RADIUS} bg-white`}
          />
        </div>
        <div className="flex-1 min-w-0">
          <TimeRangeSelector
            value={rangoHorario}
            onChange={onRangoHorarioChange}
            label={`Rango Horario`}
            startHour={9}
            endHour={21}
            intervalMinutes={30}
            required={false}
          />
        </div>
      </div>

      {/* Pago y Entrega */}
      {
        <div className="space-y-3">
          {/* Registrar Pago */}
          {!isEditMode && <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={registrarPago}
              onChange={(e) => onRegistrarPagoChange(e.target.checked)}
              className="w-5 h-5 text-emerald-600 border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
            />
            <span className="font-semibold text-slate-800">Registrar pago</span>
          </label>}
          {!isEditMode && registrarPago && (
            <div className="space-y-3">
              {bloqueComprobante}
              <div className="flex gap-3">
                <div className="flex-1 min-w-0">
                  <MoneyInput
                    label="Monto a Pagar"
                    value={montoPagoInicial}
                    onChange={onMontoPagoInicialChange}
                    min={0}
                    max={montoTotal}
                  />
                </div>
                <div className="w-36 flex-shrink-0">
                  <label htmlFor="paso2-fecha-pago" className="block text-sm font-medium text-slate-600 mb-1.5">
                    Fecha Pago
                  </label>
                  <input
                    id="paso2-fecha-pago"
                    type="date"
                    value={fechaPago}
                    onChange={(e) => onFechaPagoChange(e.target.value)}
                    className={`w-full px-2 py-3 ${FORM_BORDER} ${FORM_BORDER_HOVER} ${FOCUS_RING} ${FORM_RADIUS} bg-white`}
                  />
                </div>
              </div>
              {bloqueMedioPago}
              {precioUnitario > 0 && (
                <div>
                  <span className="text-xs text-slate-500">Elegir por unidades:</span>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {Array.from({ length: cantidadTotal + 1 }, (_, i) => i).map((qty) => (
                      <button
                        key={qty}
                        type="button"
                        onClick={() => onMontoPagoInicialChange(qty * precioUnitario)}
                        className={`px-3 py-1 text-sm rounded border transition-colors ${
                          unidadesSlider === qty
                            ? 'bg-emerald-600 text-white border-emerald-600'
                            : 'bg-white text-slate-600 border-slate-300 hover:border-emerald-400 hover:text-emerald-700'
                        }`}
                      >
                        {qty}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Divisor visual */}
          <div className="border-t border-slate-200 pt-3"></div>

          {/* Las entregas posteriores tienen su operación e historial propios. */}
          {!isEditMode && <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={registrarEntrega}
              onChange={(e) => onRegistrarEntregaChange(e.target.checked)}
              className="w-5 h-5 text-emerald-600 border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
            />
            <span className="font-semibold text-slate-800">Registrar entrega</span>
          </label>}
          {isEditMode && <p className="text-sm text-slate-600">Registra las entregas desde la acción Entregar del pedido.</p>}
          {!isEditMode && registrarEntrega && (
            <div className="space-y-3">
              <div className="flex gap-3">
                <div className="flex-1 min-w-0">
                  <TextInput
                    label="Cantidad a Entregar"
                    type="number"
                    value={String(cantidadEntregaInicial || '')}
                    onChange={(v) => onCantidadEntregaInicialChange(Number(v))}
                    min={0}
                    max={cantidadTotal}
                  />
                </div>
                <div className="w-40 flex-shrink-0">
                  <label htmlFor="paso2-fecha-entrega-real" className="block text-sm font-medium text-slate-600 mb-1.5">
                    Fecha Entrega
                  </label>
                  <input
                    id="paso2-fecha-entrega-real"
                    type="date"
                    value={fechaEntregaReal}
                    onChange={(e) => onFechaEntregaRealChange(e.target.value)}
                    className={`w-full px-3 py-3 ${FORM_BORDER} ${FORM_BORDER_HOVER} ${FOCUS_RING} ${FORM_RADIUS} bg-white`}
                  />
                </div>
              </div>
              {
                <div>
                  <span className="text-xs text-slate-500">Elegir por unidades:</span>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {Array.from({ length: cantidadTotal + 1 }, (_, i) => i).map((qty) => (
                      <button
                        key={qty}
                        type="button"
                        onClick={() => onCantidadEntregaInicialChange(qty)}
                        className={`px-3 py-1 text-sm rounded border transition-colors ${
                          cantidadEntregaInicial === qty
                            ? 'bg-emerald-600 text-white border-emerald-600'
                            : 'bg-white text-slate-600 border-slate-300 hover:border-emerald-400 hover:text-emerald-700'
                        }`}
                      >
                        {qty}
                      </button>
                    ))}
                  </div>
                </div>
              }
            </div>
          )}
        </div>
      }

      {/* Notas */}
      <TextInput
        label="Notas"
        value={notas}
        onChange={onNotasChange}
        placeholder="Información adicional..."
      />
    </div>
  );
};
